import { useEffect, useRef, useState } from "react";
import { createIdentity, importPfx, type SignatureInfo, type SigningIdentity } from "@fg/web-core/pdf";
import { deleteIdentity, listIdentities, saveIdentity } from "./identity";

/** Digital signature: the signatures already in the document and whether they hold, and signing it with a certificate. */
export function DigitalSignDialog({ pageCount, read, onSign, onClose }: {
    pageCount: number;
    read(): Promise<SignatureInfo[]>;
    onSign(id: SigningIdentity, o: { reason: string; location: string; page: number | null }): Promise<void>;
    onClose(): void;
}) {
    const [sigs, setSigs] = useState<SignatureInfo[] | null>(null);
    const [ids, setIds] = useState<SigningIdentity[]>([]);
    const [pick, setPick] = useState(0);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [reason, setReason] = useState("");
    const [location, setLocation] = useState("");
    const [visible, setVisible] = useState(true);
    const [page, setPage] = useState(1);
    const [busy, setBusy] = useState<string | null>(null);
    const [err, setErr] = useState<string | null>(null);

    useEffect(() => {
        let alive = true;
        void read().then((s) => { if (alive) setSigs(s); }).catch((e) => { if (alive) { setSigs([]); setErr(String(e?.message ?? e)); } });
        void listIdentities().then((l) => { if (alive) { setIds(l); setPick(Math.max(0, l.length - 1)); } });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const make = async () => {
        if (!name.trim()) { setErr("Enter the name to put on the certificate."); return; }
        setBusy("Creating the certificate…");
        setErr(null);
        try {
            const id = await createIdentity(name.trim(), email.trim());
            await saveIdentity(id);
            const l = await listIdentities();
            setIds(l);
            setPick(l.length - 1);
            setName("");
        } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(null); }
    };

    const fileRef = useRef<HTMLInputElement>(null);
    const [pfxPassword, setPfxPassword] = useState("");
    /** A certificate file (.pfx / .p12): its certificate and key become a signing identity kept in this browser. */
    const importFile = async (file: File) => {
        setBusy("Reading the certificate file…");
        setErr(null);
        try {
            const id = await importPfx(new Uint8Array(await file.arrayBuffer()), pfxPassword);
            await saveIdentity(id);
            const l = await listIdentities();
            setIds(l);
            setPick(l.length - 1);
            setPfxPassword("");
        } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(null); }
    };

    const sign = async () => {
        const id = ids[pick];
        if (!id) return;
        setBusy("Signing…");
        setErr(null);
        try { await onSign(id, { reason: reason.trim(), location: location.trim(), page: visible ? Math.min(Math.max(1, page), Math.max(1, pageCount)) : null }); onClose(); }
        catch (e) { setErr(e instanceof Error ? e.message : String(e)); setBusy(null); }
    };

    const field = "h-8 w-full rounded border border-border bg-background px-2 text-sm outline-none focus:ring-1 focus:ring-ring";
    const btn = "h-8 rounded border border-border bg-background px-3 text-sm hover:bg-accent disabled:opacity-50";
    const fmt = (d: Date | null) => (d ? d.toLocaleString() : "unknown");
    return (
        <div role="dialog" aria-modal="true" aria-label="Digital signature" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
            <div className="flex max-h-[90vh] w-full max-w-xl flex-col gap-3 overflow-auto rounded-xl border bg-card p-5 text-sm shadow-xl" onKeyDown={(e) => { if (e.key === "Escape" && !busy) onClose(); }}>
                <h2 className="text-base font-semibold">Digital signature</h2>

                <section aria-label="Signatures in this document" data-signatures>
                    <h3 className="text-xs font-semibold text-muted-foreground">Signatures in this document</h3>
                    {sigs === null ? <p className="text-xs text-muted-foreground">Checking…</p> : sigs.length === 0 ? <p className="text-xs text-muted-foreground">This document is not signed.</p> : (
                        <ul className="mt-1 space-y-1.5">
                            {sigs.map((s) => {
                                const ok = s.intact && s.signatureValid;
                                return (
                                    <li key={s.field} data-signature={ok ? "valid" : "invalid"} className={`rounded border p-2 text-xs ${ok ? "border-green-300 bg-green-50 dark:bg-green-950/30" : "border-red-300 bg-red-50 dark:bg-red-950/30"}`}>
                                        <div className="font-medium">{ok ? "✓ Valid" : "✗ Not valid"} — {s.signer || "unknown signer"}{s.email ? ` <${s.email}>` : ""}</div>
                                        <div className="text-muted-foreground">
                                            {s.field}; signed {fmt(s.signedAt)}{s.reason ? `; reason: ${s.reason}` : ""}{s.location ? `; ${s.location}` : ""}
                                        </div>
                                        <div className="mt-0.5">
                                            {!s.intact ? "The document was changed after this signature was made. " : ""}
                                            {s.intact && !s.signatureValid ? "The signature does not match its certificate. " : ""}
                                            {ok && !s.coversWholeFile ? "The document has been changed since it was signed (later signatures or edits). " : ""}
                                            {s.selfSigned ? "The certificate is self-signed, so its identity is not vouched for by anyone else. " : ""}
                                            {s.problem ? s.problem : ""}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </section>

                <section aria-label="Sign this document" className="space-y-2 border-t pt-3">
                    <h3 className="text-xs font-semibold text-muted-foreground">Sign this document</h3>
                    {ids.length > 0 ? (
                        <div className="flex gap-2">
                            <select aria-label="Certificate" className={field} value={pick} onChange={(e) => setPick(Number(e.target.value))}>
                                {ids.map((i, k) => <option key={i.created} value={k}>{i.name}{i.email ? ` <${i.email}>` : ""} — created {new Date(i.created).toLocaleDateString()}</option>)}
                            </select>
                            <button type="button" className={btn} title="Forget this certificate and its key" onClick={() => { const i = ids[pick]; if (i) void deleteIdentity(i.created).then(listIdentities).then((l) => { setIds(l); setPick(0); }); }}>Remove</button>
                        </div>
                    ) : <p className="text-xs text-muted-foreground">Make a certificate to sign with. It is kept in this browser; the key can sign but never be exported.</p>}
                    <div className="flex items-center gap-2" data-import-pfx>
                        <input aria-label="Certificate file password" type="password" autoComplete="off" className={field} placeholder="Password of the certificate file" value={pfxPassword} onChange={(e) => setPfxPassword(e.target.value)} />
                        <button type="button" className={`${btn} shrink-0`} disabled={Boolean(busy)} onClick={() => fileRef.current?.click()}>Import .pfx / .p12…</button>
                        <input ref={fileRef} type="file" accept=".pfx,.p12,application/x-pkcs12" className="hidden" data-pfx-input onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) void importFile(f); }} />
                    </div>
                    <details open={ids.length === 0}>
                        <summary className="cursor-pointer text-xs">Make a new certificate</summary>
                        <div className="mt-2 grid grid-cols-2 gap-2">
                            <input aria-label="Your name" className={field} placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} />
                            <input aria-label="Your email" className={field} placeholder="Email (optional)" value={email} onChange={(e) => setEmail(e.target.value)} />
                        </div>
                        <button type="button" data-make-cert className={`${btn} mt-2`} disabled={Boolean(busy)} onClick={() => void make()}>Create certificate</button>
                    </details>
                    <div className="grid grid-cols-2 gap-2">
                        <input aria-label="Reason" className={field} placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} />
                        <input aria-label="Location" className={field} placeholder="Location (optional)" value={location} onChange={(e) => setLocation(e.target.value)} />
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                        <label className="flex items-center gap-1.5"><input type="checkbox" checked={visible} onChange={(e) => setVisible(e.target.checked)} /> Show the signature on page</label>
                        <input aria-label="Page" type="number" min={1} max={Math.max(1, pageCount)} disabled={!visible} className="h-7 w-16 rounded border border-border bg-background px-2" value={page} onChange={(e) => setPage(Number(e.target.value) || 1)} />
                        <span className="text-muted-foreground">(bottom left)</span>
                    </div>
                </section>

                {busy ? <p role="status" className="text-xs text-muted-foreground">{busy}</p> : null}
                {err ? <p role="alert" className="text-xs text-red-600">{err}</p> : null}
                <div className="flex justify-end gap-2">
                    <button type="button" className={btn} disabled={Boolean(busy)} onClick={onClose}>Close</button>
                    <button type="button" data-sign-run className="h-8 rounded-md bg-primary px-3 font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50" disabled={Boolean(busy) || ids.length === 0} onClick={() => void sign()}>Sign document</button>
                </div>
            </div>
        </div>
    );
}
